import { useMemo } from "react";
import { useParallax } from "../hooks/useParallax";
import { useMousePosition } from "../hooks/useMousePosition";
import { principleMetrics } from "../data/principleMetrics";
import type { MetricValue } from "../data/principleMetrics";

interface PrincipleCardProps {
  tag: string;
  title: string;
  body: string;
  docId: string;
  isMobile: boolean;
}

export function PrincipleCard({ tag, title, body, docId, isMobile }: PrincipleCardProps) {
  // Parallax effect on scroll
  const scrollParallax = useParallax(isMobile ? 0 : 0.03);
  
  // Mouse position for hover drift
  const mousePos = useMousePosition();
  const hoverOffset = useMemo(() => {
    if (isMobile || typeof window === "undefined") {
      return { x: 0, y: 0 };
    }

    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    const maxOffset = 6;

    const offsetX = ((mousePos.x - centerX) / centerX) * maxOffset;
    const offsetY = ((mousePos.y - centerY) / centerY) * maxOffset;

    return { x: offsetX, y: offsetY };
  }, [mousePos.x, mousePos.y, isMobile]);

  const metrics = principleMetrics[docId] || [];

  return (
    <div
      className="transition-transform duration-300 ease-out will-change-transform principle-card-container h-full"
      style={{
        transform: `translateY(${scrollParallax}px) translate(${hoverOffset.x}px, ${hoverOffset.y}px)`,
      }}
    >
      <div className="relative bg-[#070B12]/70 border border-white/5 rounded-[6px] backdrop-blur-sm shadow-[0_-12px_24px_-12px_rgba(0,226,255,0.20)_inset] p-7 h-full flex flex-col">
        {/* Corner frame accents - using #00E2FF with opacity */}
        {/* Top-left corner */}
        <div className="absolute top-0 left-0 w-[24px] h-[1px] bg-[#00E2FF]/30" />
        <div className="absolute top-0 left-0 w-[1px] h-[24px] bg-[#00E2FF]/30" />
        
        {/* Bottom-right corner */}
        <div className="absolute bottom-0 right-0 w-[24px] h-[1px] bg-[#00E2FF]/30" />
        <div className="absolute bottom-0 right-0 w-[1px] h-[24px] bg-[#00E2FF]/30" />

        {/* Metric telemetry numbers background layer */}
        {metrics.length > 0 && (
          <div className="absolute inset-0 opacity-[0.04] font-mono text-[11px] leading-[1.8] pointer-events-none overflow-hidden p-7">
            {metrics.map((metric: MetricValue, idx: number) => (
              <div
                key={idx}
                className={`${
                  metric.isPositive ? "text-emerald-400/70" : "text-red-400/70"
                }`}
                style={{
                  position: "absolute",
                  top: `${20 + idx * 25}%`,
                  left: `${15 + idx * 10}%`,
                  transform: `rotate(${idx * 5}deg)`,
                }}
              >
                {metric.value} {metric.label}
              </div>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="relative z-10 flex flex-col flex-1">
          {/* Tag row */}
          <div className="font-mono text-[11px] text-white/45 tracking-wider uppercase mb-4">
            {tag}
          </div>

          {/* Title */}
          <h3 className="text-lg sm:text-xl md:text-[22px] font-serif font-semibold text-white/90 mb-3">
            {title}
          </h3>

          {/* Body - flex-1 to fill remaining space */}
          <p className="text-white/65 text-sm sm:text-[14px] leading-relaxed sm:leading-[1.55] mt-2 flex-1">
            {body}
          </p>
        </div>
      </div>
    </div>
  );
}

